import React, { useState } from 'react';
import contentData from '../data/content.json';
import { getPetMedia } from '../utils/petMediaData';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Heart, CheckCircle } from 'lucide-react';
import PetMediaCarousel from '../components/PetMediaCarousel';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';

const Adopt = () => {
  const [selectedAnimal, setSelectedAnimal] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    experience: '',
    reason: ''
  });

  // Process adoption data and add media
  const adoptionAnimals = contentData.adoption.map((pet, index) => ({
    id: index + 1,
    folder: pet.folder,
    description: pet.desc,
    media: getPetMedia(pet.folder)
  }));

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Format message for WhatsApp
    const message = `*ADOPTION APPLICATION*\n\n` +
      `*Pet ID:* #${selectedAnimal.id}\n` +
      `*Pet Folder:* ${selectedAnimal.folder}\n\n` +
      `*Applicant Details:*\n` +
      `Name: ${formData.name}\n` +
      `Email: ${formData.email}\n` +
      `Phone: ${formData.phone}\n` +
      `Address: ${formData.address}\n\n` +
      `*Previous Pet Experience:*\n${formData.experience || 'None mentioned'}\n\n` +
      `*Reason for Adoption:*\n${formData.reason}\n\n` +
      `*Pet Description:*\n${selectedAnimal.description.substring(0, 200)}...`;

    // Create WhatsApp URL
    const whatsappNumber = '919315982650';
    const encodedMessage = encodeURIComponent(message);
    const whatsappUrl = `https://wa.me/${whatsappNumber}?text=${encodedMessage}`;

    // Open WhatsApp
    window.open(whatsappUrl, '_blank');

    toast.success(`Opening WhatsApp to submit your adoption application for Pet #${selectedAnimal.id}!`);
    setFormData({ name: '', email: '', phone: '', address: '', experience: '', reason: '' });
    setDialogOpen(false);
  };

  const handleCardClick = (animal) => {
    setSelectedAnimal(animal);
    setDialogOpen(true);
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative h-[500px] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-forest-green/90 to-forest-green/70 z-10"></div>
        <img
          src="/hero-images/image.png"
          alt="Adopt Don't Shop"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="relative z-20 container mx-auto px-4 text-center text-white">
          <h1 className="text-5xl md:text-6xl font-bold font-heading mb-6">
            Adopt Don't Shop
          </h1>
          <p className="text-xl md:text-2xl max-w-3xl mx-auto mb-8">
            Give a rescued animal a loving forever home. Each adoption saves a life and making room for another rescue.
          </p>
          <div className="flex items-center justify-center space-x-2 text-lg">
            <Heart className="w-6 h-6" fill="white" />
            <span>All animals are vaccinated, sterilized, and ready for adoption</span>
          </div>
        </div>
      </section>

      {/* Adoption Process */}
      <section className="py-20 bg-sand-beige">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold font-heading text-forest-green mb-4">
              Adoption Process
            </h2>
            <p className="text-xl text-gray-700">Simple steps to welcome your new family member</p>
          </div>

          <div className="grid md:grid-cols-4 gap-8 max-w-6xl mx-auto">
            <div className="text-center">
              <div className="w-20 h-20 bg-warm-amber text-white rounded-full flex items-center justify-center text-3xl font-bold mx-auto mb-4">1</div>
              <h3 className="text-xl font-bold text-forest-green mb-2">Choose Your Pet</h3>
              <p className="text-gray-700">Browse our adoptable animals and find your perfect match</p>
            </div>
            <div className="text-center">
              <div className="w-20 h-20 bg-warm-amber text-white rounded-full flex items-center justify-center text-3xl font-bold mx-auto mb-4">2</div>
              <h3 className="text-xl font-bold text-forest-green mb-2">Submit Application</h3>
              <p className="text-gray-700">Fill out the adoption form with your details</p>
            </div>
            <div className="text-center">
              <div className="w-20 h-20 bg-warm-amber text-white rounded-full flex items-center justify-center text-3xl font-bold mx-auto mb-4">3</div>
              <h3 className="text-xl font-bold text-forest-green mb-2">Meet & Greet</h3>
              <p className="text-gray-700">Visit our facility to meet your chosen pet</p>
            </div>
            <div className="text-center">
              <div className="w-20 h-20 bg-warm-amber text-white rounded-full flex items-center justify-center text-3xl font-bold mx-auto mb-4">4</div>
              <h3 className="text-xl font-bold text-forest-green mb-2">Take Home</h3>
              <p className="text-gray-700">Complete paperwork and welcome your new friend</p>
            </div>
          </div>
        </div>
      </section>

      {/* Animals Grid */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold font-heading text-forest-green mb-4">
              Meet Our Rescues
            </h2>
            <p className="text-xl text-gray-700">Click on any pet to start your adoption journey</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
            {adoptionAnimals.map((animal) => (
              <Card
                key={animal.id}
                className="border-none shadow-lg overflow-hidden hover:shadow-2xl transition-all duration-300 group cursor-pointer transform hover:scale-[1.02]"
                onClick={() => handleCardClick(animal)}
              >
                {/* Media Carousel */}
                <PetMediaCarousel mediaItems={animal.media} />

                <CardContent className="p-6 flex flex-col h-[320px]">
                  <h3 className="text-2xl font-bold font-heading text-forest-green mb-2">
                    Pet #{animal.id}
                  </h3>
                  <div className="flex items-center space-x-2 mb-4">
                    <Badge className="bg-warm-amber text-white">Available for Adoption</Badge>
                  </div>
                  <div className="max-h-32 overflow-y-auto mb-4 pr-2 custom-scrollbar flex-1">
                    <p className="text-gray-700 whitespace-pre-line text-sm leading-relaxed">
                      {animal.description}
                    </p>
                  </div>

                  <Button
                    className="w-full bg-warm-amber hover:bg-amber-600 text-white transition-colors duration-200 mt-auto"
                  >
                    <Heart className="mr-2 w-4 h-4" />
                    Click to Adopt Me
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Adoption Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-heading text-forest-green">
              Adoption Application for Pet #{selectedAnimal?.id}
            </DialogTitle>
            <DialogDescription>
              Please fill out this form to apply for adoption. We'll review your application and contact you within 48 hours.
            </DialogDescription>
          </DialogHeader>

          {/* Display Pet Details in Dialog */}
          {selectedAnimal && (
            <div className="my-4 p-4 bg-sand-beige rounded-lg">
              <h4 className="font-bold text-forest-green mb-2">Pet Information:</h4>
              <p className="text-sm text-gray-700 whitespace-pre-line max-h-32 overflow-y-auto">
                {selectedAnimal.description}
              </p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4 mt-4">
            <div>
              <Label htmlFor="name">Full Name *</Label>
              <Input
                id="name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                required
              />
            </div>
            <div>
              <Label htmlFor="email">Email Address *</Label>
              <Input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleInputChange}
                required
              />
            </div>
            <div>
              <Label htmlFor="phone">Phone Number *</Label>
              <Input
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                required
              />
            </div>
            <div>
              <Label htmlFor="address">Complete Address *</Label>
              <Textarea
                id="address"
                name="address"
                value={formData.address}
                onChange={handleInputChange}
                required
              />
            </div>
            <div>
              <Label htmlFor="experience">Previous Pet Experience</Label>
              <Textarea
                id="experience"
                name="experience"
                value={formData.experience}
                onChange={handleInputChange}
                placeholder="Tell us about your experience with pets"
              />
            </div>
            <div>
              <Label htmlFor="reason">Why do you want to adopt this pet? *</Label>
              <Textarea
                id="reason"
                name="reason"
                value={formData.reason}
                onChange={handleInputChange}
                required
              />
            </div>
            <Button type="submit" className="w-full bg-warm-amber hover:bg-amber-600 text-white">
              Submit via WhatsApp
            </Button>
            <p className="text-xs text-gray-500 text-center mt-2">
              You will be redirected to WhatsApp to complete your application
            </p>
          </form>
        </DialogContent>
      </Dialog>


    </div>
  );
};

export default Adopt;